import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function I({
  size = 16,
  stroke = 'currentColor',
  strokeWidth = 1.5,
  children,
  ...rest
}: IconProps & { children?: React.ReactNode }) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const Icon = {
  Overview: (p: IconProps) => (
    <I {...p}>
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </I>
  ),
  Apps: (p: IconProps) => (
    <I {...p}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </I>
  ),
  Types: (p: IconProps) => (
    <I {...p}>
      <path d="M4 7h16M4 12h16M4 17h10" />
    </I>
  ),
  Alerts: (p: IconProps) => (
    <I {...p}>
      <path d="M12 2l1.5 1.5M12 6a6 6 0 0 0-6 6v3l-2 2h16l-2-2v-3a6 6 0 0 0-6-6zM10 20a2 2 0 0 0 4 0" />
    </I>
  ),
  Setup: (p: IconProps) => (
    <I {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9c.4 0 .7.2 1 .4" />
    </I>
  ),
  Search: (p: IconProps) => (
    <I {...p}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </I>
  ),
  Plus: (p: IconProps) => (
    <I {...p}>
      <path d="M12 5v14M5 12h14" />
    </I>
  ),
  X: (p: IconProps) => (
    <I {...p}>
      <path d="M18 6 6 18M6 6l12 12" />
    </I>
  ),
  ChevronDown: (p: IconProps) => (
    <I {...p}>
      <path d="m6 9 6 6 6-6" />
    </I>
  ),
  ChevronRight: (p: IconProps) => (
    <I {...p}>
      <path d="m9 6 6 6-6 6" />
    </I>
  ),
  ChevronLeft: (p: IconProps) => (
    <I {...p}>
      <path d="m15 6-6 6 6 6" />
    </I>
  ),
  Copy: (p: IconProps) => (
    <I {...p}>
      <rect x="8" y="8" width="12" height="12" rx="2" />
      <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
    </I>
  ),
  Check: (p: IconProps) => (
    <I {...p}>
      <path d="m5 12 5 5L20 7" />
    </I>
  ),
  Refresh: (p: IconProps) => (
    <I {...p}>
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
      <path d="M3 21v-5h5" />
    </I>
  ),
  Pause: (p: IconProps) => (
    <I {...p}>
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </I>
  ),
  Play: (p: IconProps) => (
    <I {...p}>
      <path d="m6 4 14 8L6 20z" />
    </I>
  ),
  Trash: (p: IconProps) => (
    <I {...p}>
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    </I>
  ),
  Edit: (p: IconProps) => (
    <I {...p}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4Z" />
    </I>
  ),
  Mail: (p: IconProps) => (
    <I {...p}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </I>
  ),
  Activity: (p: IconProps) => (
    <I {...p}>
      <path d="M3 12h4l3-7 4 14 3-7h4" />
    </I>
  ),
  Alert: (p: IconProps) => (
    <I {...p}>
      <path d="M12 9v4m0 4h.01" />
      <path d="M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    </I>
  ),
  Clock: (p: IconProps) => (
    <I {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </I>
  ),
  Filter: (p: IconProps) => (
    <I {...p}>
      <path d="M3 4h18l-7 8v6l-4 2v-8z" />
    </I>
  ),
  Server: (p: IconProps) => (
    <I {...p}>
      <rect x="3" y="4" width="18" height="6" rx="1" />
      <rect x="3" y="14" width="18" height="6" rx="1" />
      <circle cx="7" cy="7" r=".5" fill="currentColor" />
      <circle cx="7" cy="17" r=".5" fill="currentColor" />
    </I>
  ),
  Eye: (p: IconProps) => (
    <I {...p}>
      <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </I>
  ),
  LogOut: (p: IconProps) => (
    <I {...p}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5M21 12H9" />
    </I>
  ),
  Menu: (p: IconProps) => (
    <I {...p}>
      <path d="M3 6h18M3 12h18M3 18h18" />
    </I>
  ),
  Coin: (p: IconProps) => (
    <I {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 9h4.5a2 2 0 0 1 0 4H10a2 2 0 0 0 0 4h5" />
      <path d="M12 6v2M12 16v2" />
    </I>
  ),
  Spark: (p: IconProps) => (
    <I {...p}>
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4" />
      <path d="M5.5 5.5l2.8 2.8M15.7 15.7l2.8 2.8M5.5 18.5l2.8-2.8M15.7 8.3l2.8-2.8" />
    </I>
  ),
  Bell: (p: IconProps) => (
    <I {...p}>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10 21a2 2 0 0 0 4 0" />
    </I>
  ),
  Cable: (p: IconProps) => (
    <I {...p}>
      <path d="M9 2v6a3 3 0 0 0 6 0V2M7 2h4M13 2h4M9 22v-6a3 3 0 0 1 6 0v6M7 22h4M13 22h4" />
    </I>
  ),
  Sliders: (p: IconProps) => (
    <I {...p}>
      <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M2 14h4M10 8h4M18 16h4" />
    </I>
  ),
  Dots: (p: IconProps) => (
    <I {...p}>
      <circle cx="5" cy="12" r="1.4" fill="currentColor" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" />
      <circle cx="19" cy="12" r="1.4" fill="currentColor" />
    </I>
  ),
  Webhook: (p: IconProps) => (
    <I {...p}>
      <circle cx="6" cy="18" r="2" />
      <circle cx="18" cy="18" r="2" />
      <circle cx="12" cy="6" r="2" />
      <path d="M9 8 7 16M15 8l2 8M8 18h8" />
    </I>
  ),
  Slack: (p: IconProps) => (
    <I {...p}>
      <rect x="3" y="10" width="8" height="3" rx="1.5" />
      <rect x="13" y="10" width="8" height="3" rx="1.5" />
      <rect x="10" y="3" width="3" height="8" rx="1.5" />
      <rect x="10" y="13" width="3" height="8" rx="1.5" />
    </I>
  ),
  Discord: (p: IconProps) => (
    <I {...p}>
      <path d="M7 8c2-1 3-1.5 5-1.5S15 7 17 8M6 17c2 1.5 3.5 2 6 2s4-.5 6-2M5 8c-1 4-1 7 1 9M19 8c1 4 1 7-1 9" />
      <circle cx="9" cy="13" r="1" fill="currentColor" />
      <circle cx="15" cy="13" r="1" fill="currentColor" />
    </I>
  ),
  Bolt: (p: IconProps) => (
    <I {...p}>
      <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
    </I>
  ),
  Database: (p: IconProps) => (
    <I {...p}>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v6c0 1.7 4 3 9 3s9-1.3 9-3V5M3 11v6c0 1.7 4 3 9 3s9-1.3 9-3v-6" />
    </I>
  ),
  Layers: (p: IconProps) => (
    <I {...p}>
      <path d="m12 2 10 6-10 6L2 8z" />
      <path d="m2 12 10 6 10-6M2 16l10 6 10-6" />
    </I>
  ),
  Tag: (p: IconProps) => (
    <I {...p}>
      <path d="M20 12 12 20 3 11V3h8z" />
      <circle cx="7.5" cy="7.5" r="1.2" fill="currentColor" />
    </I>
  ),
  Terminal: (p: IconProps) => (
    <I {...p}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="m7 9 3 3-3 3M13 15h4" />
    </I>
  ),
  Send: (p: IconProps) => (
    <I {...p}>
      <path d="m22 2-9 20-3-9-9-3z" />
    </I>
  ),
  Type: (p: IconProps) => (
    <I {...p}>
      <path d="M4 7V5h16v2M9 20h6M12 5v15" />
    </I>
  ),
  Moon: (p: IconProps) => (
    <I {...p}>
      <path d="M21 13A8 8 0 0 1 11 3a8 8 0 1 0 10 10" />
    </I>
  ),
  Palette: (p: IconProps) => (
    <I {...p}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="7.5" cy="10.5" r=".8" fill="currentColor" />
      <circle cx="9.5" cy="6.5" r=".8" fill="currentColor" />
      <circle cx="14.5" cy="6.5" r=".8" fill="currentColor" />
      <circle cx="16.5" cy="10.5" r=".8" fill="currentColor" />
      <path d="M12 21a3 3 0 0 0 0-6 1.5 1.5 0 0 1 0-3h2a4 4 0 0 0 0-8" />
    </I>
  ),
  ArrowUp: (p: IconProps) => (
    <I {...p}>
      <path d="M12 19V5M5 12l7-7 7 7" />
    </I>
  ),
  ArrowDown: (p: IconProps) => (
    <I {...p}>
      <path d="M12 5v14M5 12l7 7 7-7" />
    </I>
  ),
};
